import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { fetchMe } from '../api/authApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { DetailField } from '../components/InfoCard';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';

const { semantic } = colorTokens;

const codeBadgeSx = {
  fontFamily: typographyTokens.fontFamilyMono,
  fontWeight: typographyTokens.fontWeightMedium,
  letterSpacing: '0.01em',
  maxWidth: '100%',
  whiteSpace: 'normal',
  height: 'auto',
  minHeight: 24,
  maxHeight: 'none',
  py: 0.5,
  lineHeight: '16px',
  textAlign: 'left',
};

const noticeSx = {
  fontFamily: typographyTokens.fontFamily,
  fontSize: 13,
  lineHeight: '18px',
  color: semantic.text.secondary,
};

function RawJsonPanel({ value }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        borderRadius: `${radiusTokens.md}px`,
        bgcolor: semantic.bg.input,
        border: `1px solid ${semantic.border.subtle}`,
        color: semantic.text.secondary,
        fontFamily: typographyTokens.fontFamilyMono,
        fontSize: 12,
        lineHeight: '18px',
        letterSpacing: '0.01em',
        whiteSpace: 'pre-wrap',
        overflowX: 'auto',
        overflowY: 'auto',
        maxWidth: '100%',
        maxHeight: 420,
      }}
    >
      {JSON.stringify(value ?? null, null, 2)}
    </Box>
  );
}

export function ProfilePage() {
  const { t } = useTranslation(['settings', 'permissions', 'common']);
  const { session } = useAuth();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMe()
      .then(setMe)
      .catch(() => setError(t('settings:profile_load_failed')))
      .finally(() => setLoading(false));
  }, [t]);

  const data = me || session;
  const emptyValue = t('common:empty_value');
  const roles = data?.roles || [];
  const permissions = data?.permissions || [];
  const visiblePermissions = permissions.slice(0, 12);
  const hiddenPermissionCount = Math.max(0, permissions.length - 12);

  if (loading && !data) {
    return <LoadingSkeleton type="Detail" />;
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="Detail"
        title={t('settings:profile_title')}
        subtitle={t('settings:profile_subtitle')}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && (
        <Stack spacing={2.5}>
          <SectionCard variant="Default" title={t('settings:profile_user_section')} sx={{ mb: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2.5,
              }}
            >
              <DetailField
                label={t('settings:profile_name')}
                value={data.user?.name || emptyValue}
              />
              <DetailField
                label={t('settings:profile_email')}
                value={data.user?.email || emptyValue}
              />
              <DetailField label={t('settings:profile_type')}>
                {data.user_type ? (
                  <StatusBadge
                    status={data.user_type}
                    category="generic"
                    apiLabel={data.user_type}
                    size="Sm"
                    tone="Neutral"
                    sx={codeBadgeSx}
                  />
                ) : emptyValue}
              </DetailField>
              <DetailField label={t('settings:profile_active')}>
                <StatusBadge
                  status={data.user?.is_active ? 'active' : 'inactive'}
                  category="generic"
                  apiLabel={data.user?.is_active ? t('common:yes') : t('common:no')}
                  size="Sm"
                  tone={data.user?.is_active ? 'Success' : 'Neutral'}
                />
              </DetailField>
            </Box>
          </SectionCard>

          <SectionCard variant="Default" title={t('settings:profile_roles')} sx={{ mb: 0 }}>
            {roles.length === 0 ? (
              <Typography sx={noticeSx}>
                {t('settings:profile_none_roles')}
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {roles.map((role) => (
                  <StatusBadge
                    key={role}
                    status={role}
                    category="generic"
                    apiLabel={role}
                    size="Sm"
                    tone="Neutral"
                    sx={codeBadgeSx}
                  />
                ))}
              </Stack>
            )}
          </SectionCard>

          <SectionCard variant="Default" title={t('permissions:systemPermissions')} sx={{ mb: 0 }}>
            <Typography sx={{ ...noticeSx, mb: 1.5 }}>
              {t('permissions:systemPermissionsNotice')}
            </Typography>
            <Typography sx={{ ...noticeSx, mb: 2 }}>
              {t('permissions:notMafertAppRolesNotice')}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {visiblePermissions.map((permission) => (
                <StatusBadge
                  key={permission}
                  status={permission}
                  category="generic"
                  apiLabel={permission}
                  size="Sm"
                  tone="Neutral"
                  sx={codeBadgeSx}
                />
              ))}
              {hiddenPermissionCount > 0 && (
                <StatusBadge
                  status="more"
                  category="generic"
                  apiLabel={t('settings:profile_more_permissions', { count: hiddenPermissionCount })}
                  size="Sm"
                  tone="Neutral"
                />
              )}
              {permissions.length === 0 && (
                <Typography sx={noticeSx}>
                  {t('common:empty_value')}
                </Typography>
              )}
            </Stack>
          </SectionCard>

          <SectionCard variant="Default" title={t('settings:profile_raw_me')} sx={{ mb: 0 }}>
            <RawJsonPanel value={data} />
          </SectionCard>
        </Stack>
      )}
    </Box>
  );
}
